import { AlternateEmail, LocalPhone } from "@mui/icons-material";
import {
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { useModalConfirm } from "@ryde/redux";
import { ConfirmResult, formatPhoneLocal, stringAvatar } from "@ryde/shared";

import { ColorIconButton } from "../../../controls";
import { EmailLink, PhoneLink } from "./contact-item.styled";
import type { IContactItemForm } from "./table-contacts.models";

const AVATAR_SIZE = 40;

export const ContactItemDisplay: FC<IContactItemForm> = ({
  removeHandler,
  index,
  onEdit,
  value,
}) => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();
  const message = t(`general.ITEM_ARCHIVE_MESSAGE`);
  const onArchiveClickEvent = useEvent(async (): Promise<void> => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message,
    });

    if (confirmationResult === ConfirmResult.OK) {
      removeHandler?.(index || 0);
    }
  });
  return (
    <ListItem
      alignItems="center"
      data-e2e="contact-item-display"
      secondaryAction={
        <>
          <ColorIconButton
            iconType="EDIT"
            edge="end"
            aria-label="edit"
            onClick={() => {
              onEdit?.();
            }}
          />
          <ColorIconButton
            iconType="DELETE"
            edge="end"
            aria-label="delete"
            onClick={() => onArchiveClickEvent()}
          />
        </>
      }
    >
      <ListItemAvatar>
        <Avatar
          alt={value?.name || t("general.NO_NAME")}
          {...stringAvatar(value?.name || t("general.NO_NAME"), AVATAR_SIZE)}
        />
      </ListItemAvatar>
      <ListItemText>
        <div>{value?.name}</div>
        {value?.jobTitle && (
          <Typography
            sx={{ display: "inline" }}
            component="span"
            variant="body2"
            color="text.primary"
          >
            {value.jobTitle}
          </Typography>
        )}
        {value?.phone && (
          <Typography
            sx={{
              display: "flex",
              alignItems: "center",
              spacing: 4,
              justifyContent: "flex-start",
            }}
          >
            <LocalPhone fontSize={"inherit"} />
            <PhoneLink target="_blank" href={"tel:" + value.phone}>
              {formatPhoneLocal(value.phone)}
            </PhoneLink>
          </Typography>
        )}
        {value?.email && (
          <Typography
            sx={{
              display: "flex",
              alignItems: "center",
              spacing: 4,
            }}
          >
            <AlternateEmail fontSize={"inherit"} />
            <EmailLink target="_blank" href={"mailto:" + value.email}>
              {value.email}
            </EmailLink>
          </Typography>
        )}
      </ListItemText>
    </ListItem>
  );
};
